<template>
  <div class="estate-item" :class="parentClass">
    <div class="estate-item__content flex">
      <div class="estate-item__ads" v-if="topOfSearch">
        <img src="@/assets/images/outstanding.png" alt="" />
      </div>
      <div class="estate-item__images">
        <div class="images__item images__main">
          <img v-if="mainImage" v-lazy="{ src: mainImage.link }" :alt="estate.estate_name" />
          <AppNoImage v-else />
        </div>
        <div class="flex">
          <div v-for="(item, index) of subImages"
            :key="item.sorting"
            class="images__item images__sub"
          >
            <img :class="index ? '__right' : '__left'"
              v-lazy="{ src: item.link }" :alt="estate.estate_name"
            />
          </div>
          <div v-for="item of 2 - subImages.length"
            :key="'no-image' + item"
            class="images__item images__sub"
          >
            <AppNoImage />
          </div>
        </div>
      </div>
      <div class="estate-item__info">
        <div class="info__main">
          <h3 @click="topOfSearch ? clickTopOfSearch(estate) : gotoBidHistory(estate)"
            class="text-md w6 mb-20 text-link four-line word-break">
            <span class="cursor-pointer" :title="estate.estate_name">{{ estate.estate_name }}</span>
          </h3>

          <div class="info__item mb-15">
            <div class="info__item__title">
              <img src="@/assets/images/location.svg" alt="info location" />
              <span>所在地</span>
            </div>
            <span :title="estate.location" class="info__item__value one-line">{{
                estate.location
            }}</span>
          </div>

          <div class="mb-min-md-15 ant-row r-flex-sm-column">
            <div v-if="estate.immediate_decision_price" class="ant-col-24"
              :class="[allowDisplayStatus ? 'ant-col-md-7' : 'ant-col-md-10']">
              <div class="info__item">
                <div class="info__item__title">
                  <img src="@/assets/images/money.svg" alt="info price" />
                  <span>即決価格</span>
                </div>
                <span class="info__item__value">{{
                    formatInJapanese(estate.immediate_decision_price * MAN_TO_YEN)
                }}</span>
              </div>
            </div>

            <div class="ant-col-24" :class="[allowDisplayStatus ? 'ant-col-md-17' : 'ant-col-md-14']">
              <div class="flex">
                <div class="info__item">
                  <div class="info__item__title">
                    <img src="@/assets/images/icon_card.svg" alt="info tag" />
                    <span>入札件数</span>
                  </div>
                  <span class="info__item__value">{{ estate.total_bidding }}</span>
                </div>

                <div class="info__item decision">
                  <div class="info__item__title">
                    <img src="@/assets/images/decision.svg" alt="info decision" />
                    <span>1入札限定</span>
                  </div>

                  <span>{{ bidTypeIcon }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="info__item mb-15">
            <div class="info__item__title">
              <img src="@/assets/images/date_time.svg" alt="info time" />
              <span>終了日時</span>
            </div>
            <span class="info__item__value">
              {{ $formatTime($convertToLocalTime(estate.end_date), DB_DATE_TIME_FORMAT) }}
            </span>
          </div>

          <div v-if="estate.won_bid_date" class="info__item mb-15">
            <div class="info__item__title">
              <img src="@/assets/images/date_time.svg" alt="info time" />
              <span>落札日時</span>
            </div>
            <span class="info__item__value">
              {{ $formatTime(estate.won_bid_date, DB_DATE_TIME_FORMAT) }}
            </span>
          </div>
        </div>

        <div class="info__footer flex flex-wrap justify-between align-center">
          <div class="flex filter__display filter__display--no-between">
            <div v-if="allowAddFavorite" class="flex justify-center align-center cursor-pointer info__footer__star"
              @click="addToFavorite">
              <Tooltip ref="tooltipRef">
                <img :src="imageStar" alt="star" class="icon-star" />
              </Tooltip>
              <span>ウォッチ</span>
            </div>

            <span v-if="allowDisplayStatus" class="badge badge-lg" :class="[estateStatus?.color_class]">
              {{ estateStatus?.text }}
            </span>

            <div v-if="allowRelist" @click="goToReExhibit"
              class="flex align-center info__footer__relist cursor-pointer">
              <img src="@/assets/images/decision_non-bg.svg" alt="decision" />
              <span class="r-text-primary2 w6">再出品</span>
            </div>
          </div>

          <div :class="[checkAllowAction ? 'mt-sm-20' : '']" class="flex justify-center align-center current_price">
            <span class="text-sm w6">{{ estate.price_text }}</span>
            <p class="text-lg20 w6 r-text-primary2 ml-10 mb-0">
              <span :class="isEstateOneTime ? 'text-md w3 r-text-dark' : ''">
                {{ estatePrice }}
              </span>
              {{ isEstateOneTime ? '非公開' : '' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { DB_DATE_TIME_FORMAT, MAN_TO_YEN } from '@/constants/common';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import useSearch from '@/uses/useSearch';
import { STATUS_COLORS } from '@/constants/estate';
import { ESTATE_RE_EXHIBIT } from '@/constants/screen';
import { formatInJapanese } from '@/utils/formatMoney';

const props = defineProps({
  estate: {
    type: Object,
    require: true,
    description: 'Estate object data',
  },
  topOfSearch: {
    type: Boolean,
    default: false,
    description: 'is advertisement top of search',
  },
});

const emit = defineEmits(['toggle-favorite']);

const route = useRoute();
const router = useRouter();
const $convertMoney = inject('$convertMoney');
const store = useStore();
const tooltipRef = ref({});

// const estateFavorite = computed(() => store.getters['estate/estateFavorite']);
const estateFavorite = computed(() => store.getters['user/estateFavorites']);
const userInfo = computed(() => store.getters['user/userInfo']);
const mainImage = computed(() => props.estate.images[0] ?? null);

const { gotoBidHistory, createBreadcrumb } = useSearch();

const parentClass = computed(() => {
  if (route.path.includes('/list-bidding/')) {
    return route.path.replace('/list-bidding/', '');
  }
  return '';
});

const isEstateOneTime = computed(() => typeof props.estate.current_price === 'string');

const subImages = computed(() => props.estate.images?.slice(1, 3) ?? null);

const bidTypeIcon = computed(() => (props.estate.bidding_type === 1 ? '✕' : '〇'));

const estatePrice = computed(() => {
  if (typeof props.estate.current_price === 'string') {
    return props.estate.current_price;
  }

  const price = props.estate.current_price ?? 0;

  return price ? $convertMoney(price * MAN_TO_YEN) : `${0}円`;
});

const imageStar = computed(() => {
  if (estateFavorite.value.includes(props.estate.bidding_id)) {
    // eslint-disable-next-line
    return require('@/assets/images/star.svg');
  }
  // eslint-disable-next-line
  return require('@/assets/images/star-white.svg');
});

const allowDisplayStatus = computed(() => route.path.includes('list-bidding'));

const allowRelist = computed(() => {
  if (!props.estate.is_re_bidding) return false;
  return true;
});

const estateStatus = computed(() => {
  if (!allowDisplayStatus.value) return '';

  return STATUS_COLORS.find((status) => status.text === props.estate.status);
});

const allowAddFavorite = !userInfo.value?.user_id_of_company?.includes(props.estate.user_id);

const checkAllowAction = computed(
  () => allowAddFavorite || allowDisplayStatus.value || allowRelist.value
);

const addToFavorite = async () => {
  const isFavorite = estateFavorite.value.includes(props.estate.bidding_id);
  await store.dispatch('estate/likeOrUnlikeEstate', { estate_bidding_id: props.estate.bidding_id });
  store.commit('user/updateFavoriteEstate', props.estate.bidding_id);
  let msg = 'ウォッチリストに追加しました。';
  if (isFavorite) {
    msg = 'ウォッチリストから削除しました。';
  }
  tooltipRef.value.show(msg);
  emit('toggle-favorite');
};
const clickTopOfSearch = (estate) => {
  const payload = {
    advertisement_detail_id: props.estate.advertisement_detail_id,
    create_by: userInfo.value.user_id,
    update_by: userInfo.value.user_id,
  };
  store.dispatch('advertising/clickAdvertising', payload);
  gotoBidHistory(estate);
};

const goToReExhibit = () => {
  const routeData = router.resolve({ name: ESTATE_RE_EXHIBIT, params: { id: props.estate.id } });
  createBreadcrumb(props.estate, props.estate.id);
  window.open(routeData.href, '_blank');
};
</script>
